import { useState } from 'react'
import { useIonAlert } from '@ionic/react'

export interface JourneyFormOptions {
  onSubmit: (payload: any) => void;
  drawRoutes: (routes: number[]) => void;
  defaultValues: any;
  defaultEmergency: any;
  defaultAccommodation: any;
}

const useJourneyForm = ({ onSubmit, drawRoutes, defaultValues, defaultEmergency, defaultAccommodation }: JourneyFormOptions) => {
  const [input, setInput] = useState(defaultValues)
  const [emergency, setEmergency] = useState(defaultEmergency)
  const [optionalInput, setOptionalInput] = useState(defaultAccommodation)
  const [showOvernight, setShowOvernight] = useState(false)
  const [ionAlert] = useIonAlert()

  const returnMessage = (key: string) => {
    if (key === 'purpose') return { message: 'Please specify the purpose of the journey' }
    if (key === 'leader' || key === 'leaderMobile') {
      return { header: 'Error - Journey Leader', message: 'Please fill in the Journey Leader’s details.' }
    }
    if (key === 'vehicleMode') {
      return { header: 'Error - Vehicle', message: 'Please select the mode of transport.' }
    }
    if (key === 'to' || key === 'arrivalTime') {
      return { header: 'Error - Arrival (estimate)', message: 'Please fill in the Arrival details.' }
    }
    if (key === 'from' || key === 'departureTime') {
      return { header: 'Error - Departure', message: 'Please fill in the Departure details.' }
    }
    if (key === 'supervisor') {
      return { header: 'Error - Authorised By', message: 'The Authorised By field is empty. Please select a user from the drop down.' }
    }
    return null
  }

  const handlePassengers = (selected: any) => {
    setInput({ ...input, passengers: selected })
  }

  const handleRoutes = (selected: any) => {
    setInput({ ...input, routes: selected })
    drawRoutes(selected.map((route: any) => route.value))
  }

  const handleInput = (e: any) => {
    setInput({ ...input, [e.target.name]: e.detail.value })
  }

  const handleOptionalInput = (e: any) => {
    setOptionalInput({ ...optionalInput, [e.target.name]: e.detail.value })
  }

  const handleSubmit = () => {
    if (!input.passengers.length) {
      ionAlert({
        header: 'Error - Passenger Details',
        message: 'You must add at least one passenger.',
        buttons: [{ text: 'Ok' }],
      })
      return
    }

    if (!input.routes.length) {
      ionAlert({
        header: 'Error - Route',
        message: 'You must select at least one route to complete a journey request.',
        buttons: [{ text: 'Ok' }],
      })
      return
    }

    const notFilled = Object.keys(input).find((key) => !input[key])
    if (notFilled && returnMessage(notFilled)) {
      const error: any = returnMessage(notFilled)
      ionAlert({
        header: error.header,
        message: error.message,
        buttons: [{ text: 'Ok' }],
      })
      return
    }

    if (emergency.emergencyPhone === '' && emergency.altEmergencyPhone === '') {
      ionAlert({
        header: 'Error - Emergency Contact',
        message: 'You must submit at least one emergency contact number.',
        buttons: [{ text: 'Ok' }],
      })
      return
    }

    if (!Object.values(optionalInput).every((key) => key === '') && !Object.values(optionalInput).every((key) => key !== '')) {
      ionAlert({
        header: 'Error',
        message: 'Fill in with all acommodation details',
        buttons: [{ text: 'Ok' }],
      })
      return
    }

    input.passengers = input.passengers.map((selected: any) => selected.value).join(', ')
    const stages = input.routes.map((route: any) => route.value)
    input.emergencyPhone = emergency.emergencyPhone

    onSubmit({
      approver: input.supervisor.id,
      purpose: input.purpose,
      leader_id: input.leader.id,
      leader_phone: input.leaderMobile,
      vehicle_mode: input.vehicleMode,
      emergency_sat: emergency.emergencyPhone,
      emergency_other: emergency.altEmergencyPhone,
      arrival_time: input.arrivalTime.format('YYYY-MM-DD HH:mm'),
      arrival_location: input.to,
      departure_time: input.departureTime.format('YYYY-MM-DD HH:mm'),
      departure_location: input.from,
      passengers: input.passengers,
      driver_location: optionalInput.driverAccommodationLocation,
      driver_contact: optionalInput.driverAccommodationPhone,
      driver_hotel: optionalInput.driverAccommodation,
      passenger_contact: optionalInput.passengerAccommodationPhone,
      passenger_hotel: optionalInput.passengerAccommodation,
      passenger_location: optionalInput.passengerAccommodationLocation,
      stages,
    })
  }

  return {
    input,
    setInput,
    emergency,
    setEmergency,
    optionalInput,
    setOptionalInput,
    showOvernight,
    setShowOvernight,
    handlePassengers,
    handleRoutes,
    handleInput,
    handleOptionalInput,
    handleSubmit,
  }
}

export default useJourneyForm
